/** Contexte de lancement d'appel vocal — partagé marketplace, profil, nav mobile.
 *  L'appel est archivé et facturé côté serveur (DO + queues) : le client
 *  n'envoie plus de CallLog/Opportunity fabriqués localement. */
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { ActiveCallScreen } from '../../../components/telephony/ActiveCallScreen';
import type { ProTenant } from '../../../types';

interface CallLauncherContextValue {
  currentPro: ProTenant | null;
  callPro: (pro: ProTenant, clientName?: string) => void;
  endCall: () => void;
}

const CallLauncherContext = createContext<CallLauncherContextValue | null>(null);

export function CallLauncherProvider({ children }: { children: React.ReactNode }) {
  const [currentPro, setCurrentPro] = useState<ProTenant | null>(null);
  const [clientName, setClientName] = useState('Client Particulier');

  const callPro = useCallback((pro: ProTenant, name?: string) => {
    setClientName(name ?? 'Client Particulier');
    setCurrentPro(pro);
  }, []);

  const endCall = useCallback(() => setCurrentPro(null), []);

  const value = useMemo(
    () => ({ currentPro, callPro, endCall }),
    [currentPro, callPro, endCall],
  );

  return (
    <CallLauncherContext.Provider value={value}>
      {children}
      {currentPro && (
        <ActiveCallScreen
          open={Boolean(currentPro)}
          pro={currentPro}
          clientName={clientName}
          onClose={endCall}
        />
      )}
    </CallLauncherContext.Provider>
  );
}

export function useCallLauncher(): CallLauncherContextValue {
  const ctx = useContext(CallLauncherContext);
  if (!ctx) throw new Error('useCallLauncher doit être utilisé dans <CallLauncherProvider>');
  return ctx;
}
